// API client: thin fetch wrapper + resource methods.
import * as outbox from "./core/outbox.js";

const TOKEN_KEY = "wt_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

const authErrorHandlers = new Set();
export function onAuthError(fn) {
  authErrorHandlers.add(fn);
  return () => authErrorHandlers.delete(fn);
}

export class ApiError extends Error {
  constructor(status, message, body) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

// Abort a stalled request instead of awaiting it forever. A phone's connection can drop a
// fetch into limbo — the socket neither completes nor errors — which would otherwise wedge a
// boot/session spinner with no recovery. An abort surfaces as a status-0 ApiError, which the
// outbox treats as retryable and screens treat as "offline / try again".
const REQUEST_TIMEOUT_MS = 15000;

async function request(method, path, body) {
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  let res, text;
  try {
    res = await fetch(path, {
      method, headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
    // Read the body under the SAME abort timer. A WebView can resolve the headers but then
    // stall the body on a wedged keep-alive socket — if the timer were cleared here the
    // res.text() below would hang forever (an unrecoverable "Loading…" spinner). Keeping the
    // timer live until the body is fully read makes the 15s timeout cover the whole request.
    text = await res.text();
  } catch (networkErr) {
    const timedOut = networkErr?.name === "AbortError";
    throw new ApiError(0, timedOut ? "Request timed out — check your connection." : "Network error — are you offline?", null);
  } finally {
    clearTimeout(timer);
  }

  if (res.status === 401) {
    setToken(null);
    for (const h of authErrorHandlers) {
      try { h(); } catch {}
    }
    if (!path.includes("/api/auth/")) {
      location.pathname = "/login";
    }
  }

  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    // A 2xx body that isn't JSON never comes from this API — it's something between us and the
    // origin answering instead (a Cloudflare interstitial, a captive portal), and handing that
    // HTML string back as "data" poisons whatever store it lands in (a session that renders as
    // undefined.exercises, etc.). Treat it as a network failure so callers retry/offline-path it.
    if (res.ok) throw new ApiError(0, "Unexpected response from the server — try again.", text);
    data = text;
  }
  if (!res.ok) {
    throw new ApiError(res.status, data?.error || `Request failed (${res.status})`, data);
  }
  return data;
}

const get = (p) => request("GET", p);
const post = (p, b) => request("POST", p, b ?? {});
const put = (p, b) => request("PUT", p, b ?? {});
const patch = (p, b) => request("PATCH", p, b ?? {});
const del = (p) => request("DELETE", p);

// Offline-resilient writes: enqueue the mutation (against an existing entity) and let
// the outbox send it in FIFO order — immediately when online, on reconnect when not.
// Callers update local state optimistically and don't depend on the response.
outbox.configure(request);
function queued(method, path, body) {
  outbox.enqueue({ method, path, body });
  outbox.flush();
  return Promise.resolve({ queued: true });
}

// Build the optimistic exercise object an offline add-exercise renders immediately, from the
// picked library row. Its id (and its one starter set's id) are client temp ids the outbox
// reconciles to real ids once the queued create replays online.
function tempExercise(lib, exTempId, setTempId) {
  return {
    id: exTempId,
    library_exercise_id: lib.id,
    workout_exercise_id: null,
    position: 9999, // appended to the end; exact value is corrected on the next server refresh
    rest_seconds: null,
    notes: null,
    superset_group: null,
    exercise_name: lib.exercise_name,
    equipment: lib.equipment ?? null,
    images: lib.images ?? [],
    metric_type: lib.metric_type || "weight_reps",
    video_url: lib.video_url ?? null,
    primary_muscle_ids: lib.primary_muscle_ids ?? [],
    secondary_muscle_ids: lib.secondary_muscle_ids ?? [],
    sets: [{
      id: setTempId, session_exercise_id: exTempId, set_index: 0,
      is_warmup: false, is_completed: false,
      weight_kg: null, reps: null, rpe: null, duration_seconds: null, distance_m: null,
    }],
    last_performance: null,
    queued: true,
  };
}

export const api = {
  trainingPlan: () => get("/api/training/plan"),
  updateTrainingWeek: (week, data) => put(`/api/training/weeks/${week}`, data),
  saveTrainingIntention: (data) => put("/api/training/intention", data),
  clearTrainingIntention: () => del("/api/training/intention"),
  weeklyReview: (week = "") => get(`/api/review${week ? `?week=${encodeURIComponent(week)}` : ""}`),

  // auth
  config: () => get("/api/config"),
  captcha: () => get("/api/auth/captcha"),
  register: (data) => post("/api/auth/register", data),
  login: (data) => post("/api/auth/login", data),
  forgotPassword: (email) => post("/api/auth/forgot", { email }),
  resetPassword: (data) => post("/api/auth/reset", data),

  // me
  me: () => get("/api/me"),
  updateMe: (data) => patch("/api/me", data),
  changePassword: (data) => post("/api/me/password", data),
  resendVerification: () => post("/api/me/resend-verification"),
  deleteAccount: () => del("/api/me"),
  exportUrl: "/api/me/export",

  // catalog
  muscles: () => get("/api/muscles"),
  exercises: (query = "") => get(`/api/exercises${query}`),
  exercise: (id) => get(`/api/exercises/${id}`),
  createExercise: (data) => post("/api/exercises", data),
  updateExercise: (id, data) => put(`/api/exercises/${id}`, data),
  exerciseHistory: (id) => get(`/api/exercises/${id}/history`),
  exercisePR: (id) => get(`/api/exercises/${id}/pr`),

  // workouts (templates)
  workouts: () => get("/api/workouts"),
  workout: (id) => get(`/api/workouts/${id}`),
  createWorkout: (data) => post("/api/workouts", data),
  updateWorkout: (id, data) => patch(`/api/workouts/${id}`, data),
  deleteWorkout: (id) => del(`/api/workouts/${id}`),
  reorderWorkouts: (order) => post("/api/workouts/reorder", { order }),
  importWorkout: (code) => post("/api/workouts/import", { code }),
  publishWorkout: (id, data) => post(`/api/workouts/${id}/publish`, data),
  unpublishWorkout: (id) => post(`/api/workouts/${id}/unpublish`),

  // discover / shared workouts
  discoverWorkouts: (query = "") => get(`/api/discover/workouts${query}`),
  sharedWorkout: (id) => get(`/api/shared-workouts/${id}`),
  copyWorkout: (id) => post(`/api/shared-workouts/${id}/copy`),
  rateWorkout: (id, data) => put(`/api/shared-workouts/${id}/rating`, data),
  unrateWorkout: (id) => del(`/api/shared-workouts/${id}/rating`),

  // social (follow graph)
  searchUsers: (q) => get(`/api/users/search?q=${encodeURIComponent(q)}`),
  friends: () => get("/api/friends"), // people I follow (accepted), with presence
  followers: () => get("/api/followers"),
  friendRequests: () => get("/api/friends/requests"),
  friendActivity: () => get("/api/friends/activity"),
  follow: (userId) => post(`/api/follows/${userId}`),
  unfollow: (userId) => del(`/api/follows/${userId}`),
  acceptFollow: (userId) => post(`/api/follows/${userId}/accept`),
  removeFollower: (userId) => del(`/api/followers/${userId}`),
  friendProfile: (userId) => get(`/api/friends/${userId}/profile`),
  giveKudos: (sessionId) => post(`/api/sessions/${sessionId}/kudos`),
  removeKudos: (sessionId) => del(`/api/sessions/${sessionId}/kudos`),

  // sessions
  // Drain first: a completion queued offline has to reach the server before a new session is
  // requested, or the old one is still active there and the start comes back 409.
  startSession: async (data) => {
    await outbox.flush();
    return post("/api/sessions", data);
  },
  activeSession: () => get("/api/sessions/active"),
  activeSessionSync: (since = 0) => get(`/api/sessions/active/sync?since=${encodeURIComponent(since)}`),
  session: (id) => get(`/api/sessions/${id}`),
  // Finishing is a mutation like any other, so it queues when there's no connection — every set
  // was already safe offline and the finish shouldn't be the one cliff in that story. FIFO keeps
  // every queued edit in front of it, and the server route is idempotent, so a replay after a
  // send that half-landed is a no-op rather than a second completion.
  //
  // The caller gets `{ queued: true }` instead of a summary and must say so plainly: the workout
  // is over on this device and not yet on the server, which is not the same as finished.
  completeSession: async (id, data) => {
    const path = `/api/sessions/${id}/complete`;
    const enqueue = () => {
      outbox.enqueue({ method: "POST", path, body: data, kind: "complete" });
      outbox.flush();
      return { queued: true };
    };
    if (outbox.shouldQueue()) return enqueue();
    try {
      return await post(path, data);
    } catch (e) {
      // The connection dropped between that check and this send. A finished workout must not be
      // lost to that window, so it joins the queue exactly as if we'd been offline all along.
      if (e?.status === 0) return enqueue();
      throw e;
    }
  },
  deleteSession: (id) => del(`/api/sessions/${id}`),
  // Training together: an explicit, revocable pairing between two ACTIVE sessions, so a set
  // finished by either partner puts the same rest on both. Never queued through the outbox —
  // this is a live arrangement between two people, and replaying a stale invitation twenty
  // minutes later (into a workout that has since ended) would be worse than failing.
  restPair: () => get("/api/sessions/active/pair"),
  invitePair: (userId) => post("/api/sessions/active/pair", { user_id: userId }),
  acceptPair: () => post("/api/sessions/active/pair/accept"),
  endPair: () => del("/api/sessions/active/pair"),
  sessionHistory: (query = "") => get(`/api/sessions${query}`),
  // Offline-resilient CREATES (see core/outbox.js). When offline (or behind a non-empty
  // queue) they enqueue with client temp ids and resolve to a real session-exercise/set
  // optimistically; online with an empty queue they hit the server directly.
  // addSessionExercise(sessionId, lib) — `lib` is a full library row (from the picker).
  addSessionExercise: (sessionId, lib) => {
    if (outbox.shouldQueue()) {
      const exTemp = outbox.nextTempId();
      const setTemp = outbox.nextTempId();
      outbox.enqueue({
        method: "POST", path: `/api/sessions/${sessionId}/exercises`, body: { library_exercise_id: lib.id },
        resolve: [{ tempId: exTemp, from: "new_exercise_id" }, { tempId: setTemp, from: "new_set_id" }],
      });
      outbox.flush();
      return Promise.resolve(tempExercise(lib, exTemp, setTemp));
    }
    return post(`/api/sessions/${sessionId}/exercises`, { library_exercise_id: lib.id }).then(
      (session) => session.exercises.find((e) => e.id === session.new_exercise_id) || session.exercises[session.exercises.length - 1]
    );
  },
  // addSet(seId, body) — body carries is_warmup + any prefill (weight/reps/…) so the queued
  // create and the optimistic row agree without a round-trip.
  addSet: (seId, body) => {
    if (outbox.shouldQueue()) {
      const tempId = outbox.nextTempId();
      outbox.enqueue({ method: "POST", path: `/api/session-exercises/${seId}/sets`, body, resolve: [{ tempId, from: "id" }] });
      outbox.flush();
      return Promise.resolve({
        id: tempId, session_exercise_id: seId, set_index: null,
        is_warmup: !!body.is_warmup, is_completed: false,
        weight_kg: body.weight_kg ?? null, reps: body.reps ?? null, rpe: null,
        duration_seconds: body.duration_seconds ?? null, distance_m: body.distance_m ?? null, queued: true,
      });
    }
    return post(`/api/session-exercises/${seId}/sets`, body);
  },
  // Offline-resilient (queued) — edits/deletes/notes/reorder of existing entities.
  updateSession: (id, data) => queued("PATCH", `/api/sessions/${id}`, data),
  updateSessionRest: (id, endsAt) => queued("PATCH", `/api/sessions/${id}/rest`, { ends_at: endsAt }),
  reorderSessionExercises: (id, order) => queued("POST", `/api/sessions/${id}/exercises/reorder`, { order }),
  removeSessionExercise: (seId) => queued("DELETE", `/api/session-exercises/${seId}`),
  updateSessionExercise: (seId, data) => queued("PATCH", `/api/session-exercises/${seId}`, data),
  // Replacement must confirm both the session and optional template write together.
  // Drain queued set edits first so already logged work cannot be reassigned.
  replaceSessionExercise: async (seId, data) => {
    await outbox.flush();
    if (outbox.pendingCount() || navigator.onLine === false) throw new Error("Reconnect and let your sets sync before replacing an exercise.");
    if (outbox.hasError()) throw new Error("Some set changes couldn't sync. Reload the session and check your logged sets before replacing.");
    return post(`/api/session-exercises/${outbox.realId(seId)}/replace`, data);
  },
  updateSet: (setId, data) => queued("PATCH", `/api/sets/${setId}`, data),
  deleteSet: (setId) => queued("DELETE", `/api/sets/${setId}`),
  // Outbox status (for the live-logger sync indicator).
  outbox,

  // programs (multi-week schedules)
  programs: () => get("/api/programs"),
  program: (id) => get(`/api/programs/${id}`),
  programToday: () => get("/api/programs/today"),
  createProgram: (data) => post("/api/programs", data),
  updateProgram: (id, data) => patch(`/api/programs/${id}`, data),
  deleteProgram: (id) => del(`/api/programs/${id}`),
  setProgramDays: (id, days) => put(`/api/programs/${id}/days`, { days }),
  advanceProgram: (id) => post(`/api/programs/${id}/advance`),
  publishProgram: (id, data) => post(`/api/programs/${id}/publish`, data),
  unpublishProgram: (id) => post(`/api/programs/${id}/unpublish`),
  discoverPrograms: (query = "") => get(`/api/discover/programs${query}`),
  sharedProgram: (id) => get(`/api/shared-programs/${id}`),
  enrollProgram: (id) => post(`/api/shared-programs/${id}/enroll`),

  // bodyweight
  bodyweight: () => get("/api/bodyweight"),
  logBodyweight: (data) => post("/api/bodyweight", data),
  deleteBodyweight: (id) => del(`/api/bodyweight/${id}`),

  // body measurements (circumferences — sibling time series to bodyweight)
  measurements: () => get("/api/measurements"),
  logMeasurement: (data) => post("/api/measurements", data), // { site, value_cm }
  deleteMeasurement: (id) => del(`/api/measurements/${id}`),
  setMeasurementGoal: (data) => put("/api/measurements/goals", data), // { site, target_cm|null }

  // stats
  dashboard: () => get("/api/dashboard"), // aggregate read for the home screen (one round-trip)
  stats: () => get("/api/stats"),
  muscleRecovery: () => get("/api/stats/muscle-recovery"),
  muscleCoverage: () => get("/api/stats/muscle-coverage"), // per-muscle {muscle_id, is_primary, is_secondary}
  records: (limit = 10) => get(`/api/stats/records?limit=${limit}`),
  muscleVolume: (weeks = 8) => get(`/api/stats/muscle-volume?weeks=${weeks}`), // volume per primary muscle over a window
  muscleSets: (weeks = 4) => get(`/api/stats/muscle-sets?weeks=${weeks}`), // avg weekly hard sets per primary muscle (hypertrophy)
  muscleDetail: (id, weeks = 8) => get(`/api/stats/muscle/${id}?weeks=${weeks}`), // per-muscle drill-down (weekly hard-set series + exercises)
  // A whole year at once. No year → the server's current UTC year (and it clamps anything odd).
  yearReview: (year) => get(`/api/stats/year${year ? `?year=${year}` : ""}`),
  achievements: () => get("/api/achievements"),

  // moderation (user-facing reporting)
  report: (data) => post("/api/reports", data), // { target_type, target_id, reason, details? }

  // admin (platform overview + account moderation — gated by users.is_admin server-side)
  adminOverview: () => get("/api/admin/overview"),
  adminSetBanned: (userId, banned) => patch(`/api/admin/users/${userId}`, { is_banned: banned }),
  adminDeleteUser: (userId) => del(`/api/admin/users/${userId}`),
  adminDeleteExercise: (id) => del(`/api/admin/exercises/${id}`),
  adminReports: (status = "open") => get(`/api/admin/reports?status=${status}`),
  adminResolveReport: (id, data) => patch(`/api/admin/reports/${id}`, data), // { status, note? }
  adminInvites: () => get("/api/admin/invites"),
  adminCreateInvite: (data) => post("/api/admin/invites", data), // { note?, expires_in_days? }
  adminDeleteInvite: (id) => del(`/api/admin/invites/${id}`),

  // health connect / Mi Band (native bridge — see lib/integrations/health.js)
  healthMintToken: () => post("/api/integrations/health/token"), // → { token } (scoped device token)
  healthRevokeToken: () => del("/api/integrations/health/token"),
  ingestHealth: (workouts) => post("/api/integrations/health/sessions", { workouts }),
  appendLiveHr: (samples) => post("/api/integrations/health/sessions/active/hr", { samples }),

  // web push
  pushKey: () => get("/api/push/key"),
  pushSubscribe: (subscription) => post("/api/push/subscribe", { subscription }),
  pushUnsubscribe: (endpoint) => post("/api/push/unsubscribe", { endpoint }),
  pushTest: () => post("/api/push/test"),

  // native push (Android shell, FCM)
  fcmRegister: (token) => post("/api/push/fcm/register", { token }),
  fcmUnregister: (token) => post("/api/push/fcm/unregister", { token }),
};
